import React, { createContext, useState, useRef, useEffect, useMemo } from 'react';
import styles from './index.module.scss';
import Header from './components/Header';
import LeftBox from './components/LeftBox';
import CenterBox from './components/CenterBox';
import RightBox from './components/RightBox';
import TotalEnergy from './components/CenterBox/TotalEnergy';
import WorkshopStats from './components/CenterBox/WorkshopStats';
import mockData from './mock';
import dayjs, { Dayjs } from 'dayjs';
import { IndexTimeActive } from './components/SwitchDate';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { useSize } from 'ahooks';
// @ts-ignore
import lastBg from './imgs/last_bg.png';
// @ts-ignore
import mainBuildingBg from './imgs/main_building_bg.png';
// @ts-ignore
import buildingPositionBg from './imgs/building_position_bg.png';
// @ts-ignore
import deviceLegendBg from './imgs/device_legend_bg.png';
import BuildingLineBg from './components/BuildingLineBg';

export interface ShareDataWrapperProps {
  isFullScreen: boolean;
  isStandardSize: boolean;
}

export const ShareDataWrapperContext = createContext<ShareDataWrapperProps>({
  isFullScreen: false,
  isStandardSize: true,
});

/** 日期选择 Context */
export interface DateContextProps {
  activeKey: IndexTimeActive;
  selectedDate: Dayjs;
  setActiveKey: (key: IndexTimeActive) => void;
  setSelectedDate: (date: Dayjs) => void;
}

export const DateContext = createContext<DateContextProps>({
  activeKey: IndexTimeActive.THIS_MONTH,
  selectedDate: dayjs(),
  setActiveKey: () => {},
  setSelectedDate: () => {},
});

export interface PaintingDataProps {
  energyStats: typeof mockData.energyStats;
  energyCostTable: typeof mockData.energyCostTable;
  deviceTypeEnergyRatio: typeof mockData.deviceTypeEnergyRatio;
  energyTrend: typeof mockData.energyTrend;
  totalEnergyData: typeof mockData.totalEnergyData;
  workshopStats: typeof mockData.workshopStats;
  highEnergyDevice: typeof mockData.highEnergyDevice;
  highEnergyDeviceRatio: typeof mockData.highEnergyDeviceRatio;
  highEnergyTrend: typeof mockData.highEnergyTrend;
  deviceDetail: typeof mockData.deviceDetail;
  dehumidifierPhases: typeof mockData.dehumidifierPhases;
  vocsPhases: typeof mockData.vocsPhases;
  vocsSummary: typeof mockData.vocsSummary;
  workshopSpanData: typeof mockData.workshopSpanData;
  workshopDetailSummary: typeof mockData.workshopDetailSummary;
  workshopTrendData: typeof mockData.workshopTrendData;
  workshopCostBreakdown: typeof mockData.workshopCostBreakdown;
}

export const PaintingDataContext = createContext<PaintingDataProps>({
  energyStats: mockData.energyStats,
  energyCostTable: mockData.energyCostTable,
  deviceTypeEnergyRatio: mockData.deviceTypeEnergyRatio,
  energyTrend: mockData.energyTrend,
  totalEnergyData: mockData.totalEnergyData,
  workshopStats: mockData.workshopStats,
  highEnergyDevice: mockData.highEnergyDevice,
  highEnergyDeviceRatio: mockData.highEnergyDeviceRatio,
  highEnergyTrend: mockData.highEnergyTrend,
  deviceDetail: mockData.deviceDetail,
  dehumidifierPhases: mockData.dehumidifierPhases,
  vocsPhases: mockData.vocsPhases,
  vocsSummary: mockData.vocsSummary,
  workshopSpanData: mockData.workshopSpanData,
  workshopDetailSummary: mockData.workshopDetailSummary,
  workshopTrendData: mockData.workshopTrendData,
  workshopCostBreakdown: mockData.workshopCostBreakdown,
});

const setFitScale = (
  targetContainer?: HTMLDivElement,
  currentClientWidth?: number,
  currentClientHeight?: number,
  isFullScreen?: boolean,
  isStandardSize?: boolean
) => {
  if (!targetContainer || !currentClientWidth || !currentClientHeight) return;
  const designWidth = isStandardSize ? 1920 : 2970;
  const scale = currentClientWidth / designWidth;
  // 非全屏时减去外部间距(50px)
  const containerHeight = isFullScreen
    ? document.documentElement.clientHeight
    : document.documentElement.clientHeight - 50;

  // 大屏布局：屏幕宽度 > 2560 时始终缩放
  if (!isStandardSize || currentClientWidth >= designWidth) {
    targetContainer.style.width = `${designWidth}px`;
    targetContainer.style.height = `${containerHeight / scale}px`;
    targetContainer.style.transform = `scale3d(${scale}, ${scale}, ${scale})`;
    targetContainer.style.transformOrigin = 'top left';
    targetContainer.style.flex = '';
    document.documentElement.style.setProperty('--page-scale', String(scale));
    return;
  }
  targetContainer.style.width = '';
  targetContainer.style.height = '';
  targetContainer.style.transform = '';
  targetContainer.style.transformOrigin = '';
};

const PaintingWorkshopOverview: React.FC = () => {
  const baseDivRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isStandardSize, setIsStandardSize] = useState<boolean>(true);
  const [showDeviceLegend, setShowDeviceLegend] = useState<boolean>(false);
  const size = useSize(baseDivRef);

  // 点击底图空白区域切换设备图例显示
  const handleBgClick = (e: React.MouseEvent) => {
    // 只允许点击 center_area 本身触发切换，避免点击子元素（header_energy、footer_workshop）触发
    if (e.target === e.currentTarget) {
      setShowDeviceLegend(prev => !prev);
    }
  };

  // 日期状态（提升到顶层，供 LeftBox 和 RightBox 共享）
  const [activeKey, setActiveKey] = useState<IndexTimeActive>(IndexTimeActive.THIS_MONTH);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  // 判断是否为标准尺寸2560px
  useEffect(() => {
    if (size?.width && size.width > 2560) {
      setIsStandardSize(false);
    } else {
      setIsStandardSize(true);
    }
  }, [size?.width]);

  // 缩放处理
  useEffect(() => {
    const handleResize = () => {
      setFitScale(
        contentRef.current ?? undefined,
        document.documentElement.clientWidth,
        document.documentElement.clientHeight,
        isFullScreen,
        isStandardSize
      );
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullScreen, isStandardSize]);

  // 全屏监听
  useEffect(() => {
    const handleFullScreen = () => setIsFullScreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', handleFullScreen);
    return () => document.removeEventListener('fullscreenchange', handleFullScreen);
  }, []);

  // 全屏切换
  const fullBtn = () => {
    if (baseDivRef.current) {
      isFullScreen ? document.exitFullscreen() : baseDivRef.current.requestFullscreen();
    }
  };

  const paintingData: PaintingDataProps = {
    energyStats: mockData.energyStats,
    energyCostTable: mockData.energyCostTable,
    deviceTypeEnergyRatio: mockData.deviceTypeEnergyRatio,
    energyTrend: mockData.energyTrend,
    totalEnergyData: mockData.totalEnergyData,
    workshopStats: mockData.workshopStats,
    highEnergyDevice: mockData.highEnergyDevice,
    highEnergyDeviceRatio: mockData.highEnergyDeviceRatio,
    highEnergyTrend: mockData.highEnergyTrend,
    deviceDetail: mockData.deviceDetail,
    dehumidifierPhases: mockData.dehumidifierPhases,
    vocsPhases: mockData.vocsPhases,
    vocsSummary: mockData.vocsSummary,
    workshopSpanData: mockData.workshopSpanData,
    workshopDetailSummary: mockData.workshopDetailSummary,
    workshopTrendData: mockData.workshopTrendData,
    workshopCostBreakdown: mockData.workshopCostBreakdown,
  };

  return (
    <div className={styles.outer_wrap} id="full_screen_box" ref={baseDivRef}>
      <div className={styles.inner} ref={contentRef}>
        <div className={styles.inner_top}>
          <Header fullBtn={fullBtn} isFullScreen={isFullScreen} />
        </div>
        <ShareDataWrapperContext.Provider value={{ isFullScreen, isStandardSize }}>
          <DateContext.Provider value={{ activeKey, selectedDate, setActiveKey, setSelectedDate }}>
            <PaintingDataContext.Provider value={paintingData}>
              <div className={`${styles.inner_center} ${isFullScreen ? styles.full_screen : ''}`}>
                {isStandardSize ? (
                  // 标准尺寸（≤2560px）：三栏布局
                  <div className={`${styles.content_layer} ${isFullScreen ? styles.full_screen_layer : ''}`}>
                    {/* 左栏 */}
                    <div className={styles.column_box}>
                      <LeftBox />
                    </div>
                    {/* 中栏 */}
                    <div className={styles.column_box}>
                      <CenterBox />
                    </div>
                    {/* 右栏 */}
                    <div className={styles.column_box}>
                      <RightBox />
                    </div>
                  </div>
                ) : (
                  // 大屏布局（>2560px）
                  <div className={`${styles.large_content_layer} ${isFullScreen ? styles.full_screen_layer : ''}`}>
                    {/* 图片背景层 */}
                    <div className={styles.video_bg_layer}>
                      <img src={lastBg} alt="背景" />
                    </div>
                    {/* 主建筑背景层 */}
                    <div className={styles.main_building_layer}>
                      <img src={mainBuildingBg} alt="主建筑" />
                    </div>
                    {/* 建筑描边线条层 */}
                    <div className={styles.building_line_layer}>
                      <BuildingLineBg />
                    </div>
                    {/* 建筑位置层 */}
                    <div className={styles.building_position_layer}>
                      <img src={buildingPositionBg} alt="建筑位置" />
                    </div>
                    {/* 设备图例层（点击底图切换显示） */}
                    {showDeviceLegend && (
                      <div className={styles.device_legend_layer}>
                        <img src={deviceLegendBg} alt="设备图例" />
                      </div>
                    )}
                    {/* 主体区域 */}
                    <div className={styles.main_content}>
                      {/* 左侧固定宽度 */}
                      <div className={styles.left_panel}>
                        <LeftBox />
                      </div>
                      {/* 中间区域 */}
                      <div className={styles.center_area} onClick={handleBgClick}>
                        {/* 头部：总能耗数据 */}
                        <div className={styles.header_energy}>
                          <TotalEnergy />
                        </div>
                        {/* 底部：车间用量数据 */}
                        <div className={styles.footer_workshop}>
                          <WorkshopStats />
                        </div>
                      </div>
                      {/* 右侧固定宽度 */}
                      <div className={styles.right_panel}>
                        <RightBox />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </PaintingDataContext.Provider>
          </DateContext.Provider>
        </ShareDataWrapperContext.Provider>
      </div>
    </div>
  );
};

export default PaintingWorkshopOverview;
